/**
 * Playlist playback hook for local videos.
 * Derived from ryos `useVideosLogic` (AGPL-3.0), stripped of
 * YouTube / store / i18n dependencies.
 */
import { useRef, useState, useCallback, useEffect } from 'react';

const PLAYLIST = [
  { id: 0, url: '/creative-hours-may.mp4', title: 'creative-hours-may' },
  { id: 1, url: '/teaser-trailer-v3.mp4', title: 'teaser-trailer-v3' },
  { id: 2, url: '/creative-hours-draft.mp4', title: 'creative-hours-draft' },
];

const SWIPE_MOVE_THRESHOLD = 10;

export function formatRyosTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function useRyosVideosPlayer() {
  const playerRef = useRef(null);
  const touchGestureRef = useRef(null);
  const prevIsPlayingRef = useRef(false);
  const autoShowHoverResetRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isVideoHovered, setIsVideoHovered] = useState(false);
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const [dragSeekTime, setDragSeekTime] = useState(0);
  const [statusMessage, setStatusMessage] = useState(null);
  const [animationDirection, setAnimationDirection] = useState('next');
  const statusTimeoutRef = useRef(null);

  const currentVideo = PLAYLIST[currentIndex];
  const trackNumber = currentIndex + 1;
  const trackCount = PLAYLIST.length;

  const displayTime =
    isDraggingSeek && Number.isFinite(dragSeekTime)
      ? dragSeekTime
      : playedSeconds;

  // --- status helper ---
  const showStatus = useCallback(msg => {
    setStatusMessage(msg);
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    statusTimeoutRef.current = setTimeout(() => setStatusMessage(null), 2000);
  }, []);

  // --- progress / duration ---
  const handleProgress = useCallback(
    state => {
      if (isDraggingSeek) return;
      setPlayedSeconds(state.playedSeconds || 0);
      setElapsedTime(Math.floor(state.playedSeconds || 0));
    },
    [isDraggingSeek],
  );

  const handleDuration = useCallback(d => setDuration(d || 0), []);

  // --- seek ---
  const handleSeek = useCallback(time => {
    const p = playerRef.current;
    if (p && typeof p.seekTo === 'function') {
      p.seekTo(time, 'seconds');
    }
  }, []);

  // --- track navigation ---
  const goToTrack = useCallback(
    (index, dir) => {
      setAnimationDirection(dir);
      setCurrentIndex(index);
      setPlayedSeconds(0);
      setElapsedTime(0);
      setDuration(0);
      setIsPlaying(true);
      // Safari needs a direct play() call after source change;
      // wait a tick for ReactPlayer to update the <video> src.
      setTimeout(() => {
        const rp = playerRef.current;
        const video = rp && typeof rp.getInternalPlayer === 'function'
          ? rp.getInternalPlayer()
          : null;
        if (video && typeof video.play === 'function') {
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      }, 100);
    },
    [],
  );

  const nextVideo = useCallback(() => {
    if (currentIndex < PLAYLIST.length - 1) {
      showStatus('Next ▶▶');
      goToTrack(currentIndex + 1, 'next');
    } else {
      // wrap to first
      showStatus('Repeating playlist');
      goToTrack(0, 'next');
    }
  }, [currentIndex, goToTrack, showStatus]);

  const previousVideo = useCallback(() => {
    // if more than 3 seconds in, restart current track
    if (playedSeconds > 3) {
      handleSeek(0);
      setPlayedSeconds(0);
      setElapsedTime(0);
      showStatus('⏮ Restart');
      return;
    }
    if (currentIndex > 0) {
      showStatus('⏮ Previous');
      goToTrack(currentIndex - 1, 'prev');
    } else {
      // wrap to last
      showStatus('⏮ Previous');
      goToTrack(PLAYLIST.length - 1, 'prev');
    }
  }, [currentIndex, playedSeconds, goToTrack, handleSeek, showStatus]);

  // --- end → next track ---
  const handleVideoEnd = useCallback(() => {
    if (currentIndex < PLAYLIST.length - 1) {
      goToTrack(currentIndex + 1, 'next');
    } else {
      // loop back to start
      goToTrack(0, 'next');
    }
  }, [currentIndex, goToTrack]);

  // --- toggle play (direct <video> calls keep Safari inside the user-gesture stack) ---
  const togglePlay = useCallback(() => {
    const rp = playerRef.current;
    // getInternalPlayer() returns the raw <video> element for file-based players
    const video = rp && typeof rp.getInternalPlayer === 'function'
      ? rp.getInternalPlayer()
      : null;
    if (video && typeof video.play === 'function') {
      if (video.paused) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
      return;
    }
    // fallback if internal player not ready yet
    setIsPlaying(v => !v);
  }, []);

  // --- drag-seek callback for SeekBar ---
  const onDragSeekChange = useCallback((dragging, seekTime) => {
    setIsDraggingSeek(dragging);
    if (seekTime !== undefined) setDragSeekTime(seekTime);
  }, []);

  // --- overlay pointer handlers (tap / swipe on video) ---
  const handleOverlayPointerDown = useCallback(e => {
    if (e.pointerType === 'touch') {
      touchGestureRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        pointerId: e.pointerId,
      };
      setIsVideoHovered(true);
    }
  }, []);

  const handleOverlayPointerMove = useCallback(e => {
    if (e.pointerType !== 'touch' || !touchGestureRef.current) return;
    const dx = Math.abs(e.clientX - touchGestureRef.current.startX);
    const dy = Math.abs(e.clientY - touchGestureRef.current.startY);
    if (
      !touchGestureRef.current.moved &&
      (dx > SWIPE_MOVE_THRESHOLD || dy > SWIPE_MOVE_THRESHOLD)
    ) {
      touchGestureRef.current.moved = true;
      setIsVideoHovered(true);
    }
  }, []);

  const handleOverlayPointerUp = useCallback(
    e => {
      if (e.pointerType === 'touch') {
        e.preventDefault();
        e.stopPropagation();
        const wasSwipe =
          touchGestureRef.current && touchGestureRef.current.moved;
        touchGestureRef.current = null;
        setIsVideoHovered(false);
        if (!wasSwipe) togglePlay();
        return;
      }
      togglePlay();
    },
    [togglePlay],
  );

  const handleOverlayPointerCancel = useCallback(() => {
    touchGestureRef.current = null;
    setIsVideoHovered(false);
  }, []);

  // --- auto-show seekbar when playback starts ---
  useEffect(() => {
    const wasPlaying = prevIsPlayingRef.current;
    if (!wasPlaying && isPlaying) {
      setIsVideoHovered(true);
      if (autoShowHoverResetRef.current)
        clearTimeout(autoShowHoverResetRef.current);
      autoShowHoverResetRef.current = setTimeout(
        () => setIsVideoHovered(false),
        150,
      );
    }
    prevIsPlayingRef.current = isPlaying;
    return () => {
      if (autoShowHoverResetRef.current) {
        clearTimeout(autoShowHoverResetRef.current);
        autoShowHoverResetRef.current = null;
      }
    };
  }, [isPlaying]);

  // --- cleanup ---
  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    };
  }, []);

  return {
    playerRef,
    videoUrl: currentVideo.url,
    videoTitle: currentVideo.title,
    trackNumber,
    trackCount,
    currentIndex,
    animationDirection,
    isPlaying,
    setIsPlaying,
    duration,
    playedSeconds,
    elapsedTime,
    displayTime,
    isVideoHovered,
    setIsVideoHovered,
    isDraggingSeek,
    dragSeekTime,
    statusMessage,
    handleProgress,
    handleDuration,
    handleSeek,
    handleVideoEnd,
    togglePlay,
    nextVideo,
    previousVideo,
    onDragSeekChange,
    showStatus,
    handleOverlayPointerDown,
    handleOverlayPointerMove,
    handleOverlayPointerUp,
    handleOverlayPointerCancel,
  };
}
